// -----------------------------------------------------------------------------
// ActionPanel  (Phase 5.2 — full panel routing, #26I sidebar email)
//
// Slide-in panel that renders to the right of the guided checklist when the
// user taps an action CTA (UpNextCard primary button or a row click).
//
// Layout: the parent orchestrator (AttendedGuidedChecklist / V2) shrinks the
// checklist column to 43% and renders this panel at 57% beside it.
//
// Routing:
//   EMAIL_SENT / SEND_TREATMENT_PLAN / SEND_REVIEW_REQUEST
//       → calls onOpenSidebarTab('email') on mount + renders SidebarRedirectConfirmation
//         (known minor regression: linked_template_id is no longer auto-selected in
//          the sidebar composer; acceptable — user explicitly wants the sidebar UI)
//   All other known types → matched panel from PANEL_MAP (panels/index.js)
//   Unknown types → ComingSoonStub (defensive fallback only)
//
// Props:
//   item             — the checklist / UpNext item that was actioned
//   practiceId       — forwarded to panel
//   leadId           — forwarded to panel
//   leadDetail       — forwarded to panel
//   onClose          — called when user cancels
//   onSendSuccess    — unused for sidebar path; kept for API compatibility
//   onComplete       — called after panel success; parent marks item done + refresh
//   onOpenSidebarTab — called with 'email'|'whatsapp' to activate sidebar tab
// -----------------------------------------------------------------------------
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { Box, Typography, IconButton, Button, useMediaQuery } from '@mui/material';
import { FocusTrap } from '@mui/base/FocusTrap';
import Iconify from 'src/components/Iconify';
import { callApi } from 'src/utils/apiCall';
import { parseWtmResolveResponse } from 'src/components/whatsappComponents/quickReplyUtils';
import { PANEL_MAP } from './panels';
import SidebarRedirectConfirmation from './panels/SidebarRedirectConfirmation';

const EASING = 'cubic-bezier(0.32, 0.72, 0, 1)';

// Action types that open the email composer
export const EMAIL_ACTION_TYPES = new Set([
    'EMAIL_SENT',
    'SEND_TREATMENT_PLAN',
    'SEND_REVIEW_REQUEST',
]);

// 2026-06-07 — Action types that open the WhatsApp composer in the right-side
// sidebar tab (mirrors EMAIL_ACTION_TYPES pattern). Previously WHATSAPP_SENT
// fell through to PANEL_MAP → WhatsAppPanel → IosWhatsAppComposerDialog (a
// centred modal). Routing it through SidebarRedirectConfirmation lands the user
// in the sidebar WhatsApp tab (already wired in both Attended popups and
// FollowUpV2 — OpenAttendedPopup.jsx:197, OpenConsultationPopup.jsx:204,
// FollowUpV2.jsx:2055). Same UX as email: composer opens in the sidebar, user
// sends from there, comes back and marks the item done.
export const WHATSAPP_ACTION_TYPES = new Set([
    'WHATSAPP_SENT',
]);

// ---------------------------------------------------------------------------
// Stub shown for non-email action types
// ---------------------------------------------------------------------------
function ComingSoonStub({ item, onClose }) {
    const labelMap = {
        PHONE_CALLED:      'Log call',
        LOG_PHONE_CALL:    'Log call',
        WHATSAPP_SENT:     'Send WhatsApp',
        SMS_SENT:          'Send SMS',
        SEND_CONSENT_FORM: 'Send consent form',
        SEND_PAYMENT_LINK: 'Send payment link',
        SCHEDULE_REMINDER: 'Set reminder',
        ADD_NOTE:          'Add note',
        FREE_TEXT:         'Enter text',
    };
    const label = labelMap[item?.item_type] || item?.title || 'Take action';

    return (
        <Box
            data-testid="action-panel-stub"
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                px: 3,
                py: 4,
                gap: 2,
                textAlign: 'center',
            }}
        >
            <Box
                sx={{
                    width: 52,
                    height: 52,
                    borderRadius: '14px',
                    bgcolor: 'rgba(7,141,238,0.08)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Iconify icon="mdi:tools" width={26} height={26} sx={{ color: '#078DEE' }} />
            </Box>
            <Box>
                <Typography sx={{ fontSize: '15px', fontWeight: 700, color: '#1d1d1f', letterSpacing: '-0.02em' }}>
                    {label}
                </Typography>
                <Typography
                    data-testid="action-panel-stub-message"
                    sx={{ fontSize: '13px', color: '#6e6e73', mt: 0.5, letterSpacing: '-0.005em', px: 1 }}
                >
                    This action isn&apos;t wired up yet. Mark it complete from the checklist row when done.
                </Typography>
            </Box>
            <Button
                variant="outlined"
                size="small"
                onClick={onClose}
                sx={{ textTransform: 'none', borderRadius: '8px', fontSize: '13px' }}
            >
                Close
            </Button>
        </Box>
    );
}
ComingSoonStub.propTypes = {
    item: PropTypes.object,
    onClose: PropTypes.func.isRequired,
};

// ---------------------------------------------------------------------------
// ActionPanel
// ---------------------------------------------------------------------------

export default function ActionPanel({
    item,
    practiceId,
    leadId,
    leadDetail,
    onClose,
    onComplete,
    handleReloadLeads,
    onOpenSidebarTab,
    noSidebar,
    callScriptCategory,
    hideHeader,
}) {
    const [mounted, setMounted] = useState(false);
    const panelRef = useRef(null);
    const isEmail = item && EMAIL_ACTION_TYPES.has(item.item_type);
    const isWhatsapp = item && WHATSAPP_ACTION_TYPES.has(item.item_type);
    // Below 900px the panel collapses to full width (stacks vertically over the checklist).
    const isNarrow = useMediaQuery('(max-width: 900px)');

    // Trigger entrance animation after first paint.
    useEffect(() => {
        const t = setTimeout(() => setMounted(true), 16);
        return () => clearTimeout(t);
    }, []);

    // Move focus to the first focusable element inside the panel after it appears.
    useEffect(() => {
        if (!mounted) return undefined;
        const t = setTimeout(() => {
            const focusable = panelRef.current?.querySelector(
                'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
            );
            focusable?.focus();
        }, 50);
        return () => clearTimeout(t);
    }, [mounted]);

    // For email action types open the sidebar Email tab immediately on mount.
    // Fires once per panel open; item_type is stable during a panel's lifetime.
    //
    // ETM: if this item maps to a per-practice email template, resolve it and
    // hand the template id to the sidebar composer so it preloads (fixes the
    // "linked_template_id no longer auto-selected" regression). Opens the tab
    // immediately for snappy UX; applies the resolved template when it lands.
    // Uses the lead's OWN practice id — never a caller-supplied value.
    useEffect(() => {
        if (!isEmail || !onOpenSidebarTab) return undefined;
        onOpenSidebarTab('email');
        const resolvePracticeId = leadDetail?.practice || leadDetail?.practice_id || practiceId;
        const templateItemId = item?.template_item;
        if (!templateItemId || !resolvePracticeId) return undefined;
        let active = true;
        callApi(`/api/v1/practices/${resolvePracticeId}/checklist-email-mappings/resolve/?template_checklist_item=${templateItemId}`)
            .then((res) => {
                if (!active) return;
                const id = res?.data?.[0]?.engagement_template_id ?? null;
                if (id != null) onOpenSidebarTab('email', { templateId: id });
            })
            .catch(() => { /* silent — composer stays on its default (blank) */ });
        return () => { active = false; };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // 2026-06-07 — WHATSAPP_SENT (task #89): mirror the email resolve above.
    // WTM: if this item maps to a per-practice WhatsApp template, resolve it and
    // hand the template { name, id } to the sidebar WhatsApp composer so it
    // preloads. Same shape as the email useEffect at ActionPanel.jsx:178-194 —
    // open the tab immediately for snappy UX, apply the resolved template when
    // it lands.
    //
    // Payload shape MUST match FollowUpPanel's resolveMappingForChannel
    // (panels/FollowUpPanel.jsx:335) — { templateName, templateWaId, itemId } —
    // because resolveWhatsappSidebarTemplate() in quickReplyUtils.js only reads
    // those two keys (opts.templateId was previously sent here and silently
    // ignored, leaving the sidebar on the generic "Please select a template"
    // picker). Use the shared parseWtmResolveResponse() so both call sites parse
    // the `/checklist-whatsapp-mappings/resolve/` response identically.
    useEffect(() => {
        if (!isWhatsapp || !onOpenSidebarTab) return undefined;
        onOpenSidebarTab('whatsapp');
        const resolvePracticeId = leadDetail?.practice || leadDetail?.practice_id || practiceId;
        const templateItemId = item?.template_item;
        if (!templateItemId || !resolvePracticeId) return undefined;
        const rowItemId = item?.id ?? templateItemId;
        let active = true;
        callApi(`/api/v1/practices/${resolvePracticeId}/checklist-whatsapp-mappings/resolve/?template_checklist_item=${templateItemId}`)
            .then((res) => {
                if (!active) return;
                const wtm = parseWtmResolveResponse(res);
                if (wtm) onOpenSidebarTab('whatsapp', { templateName: wtm.name, templateWaId: wtm.id, itemId: rowItemId });
            })
            .catch(() => { /* silent — composer stays on its default (blank) */ });
        return () => { active = false; };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    if (!item) return null;

    // disableEnforceFocus always: the outer Dialog (AttendedFullscreenWrapper) already
    // provides modal-level focus containment. Enforcing focus inside this inner trap
    // intercepts pointer events on sibling elements in the same modal — specifically
    // ChecklistMiniGrid cards — preventing the user from clicking a different card to
    // switch panel content. The email composer case (sidebar subtree outside DOM) is
    // covered by the same flag.
    return (
        <FocusTrap open disableEnforceFocus>
        <Box
            ref={panelRef}
            tabIndex={-1}
            data-testid="action-panel"
            data-item-type={item.item_type}
            role="dialog"
            aria-modal="true"
            aria-label={item.title || 'Action panel'}
            sx={{
                flex: isNarrow ? '0 0 100%' : '0 0 40%',
                minWidth: 0,
                bgcolor: '#ffffff',
                borderRadius: '16px',
                border: '1px solid rgba(0,0,0,0.08)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                // Opacity-only entrance — dropping the translateX prevents input hit-area
                // shifts mid-animation (same class of issue fixed in AttendedFullscreenWrapper).
                opacity: mounted ? 1 : 0,
                transition: `opacity 240ms ${EASING}`,
            }}
        >
            {/* Panel header (hosts that render their own title row, e.g. NextTaskCardDialog, pass hideHeader) */}
            {!hideHeader && (
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 2,
                    py: 1.25,
                    borderBottom: '1px solid rgba(0,0,0,0.06)',
                    flexShrink: 0,
                }}
            >
                <Typography
                    sx={{
                        fontSize: '13px',
                        fontWeight: 600,
                        color: '#1d1d1f',
                        letterSpacing: '-0.01em',
                    }}
                >
                    {item.title || item.name || 'Action'}
                </Typography>
                <IconButton
                    data-testid="action-panel-close"
                    size="small"
                    aria-label="Close panel"
                    onClick={onClose}
                    sx={{
                        p: 0.5,
                        color: '#6e6e73',
                        '&:hover': { color: '#1d1d1f', bgcolor: 'rgba(0,0,0,0.04)' },
                    }}
                >
                    <Iconify icon="mdi:close" width={16} height={16} />
                </IconButton>
            </Box>
            )}

            {/* Panel body — routed by item_type.
                 overflowX:hidden clips horizontal bleed (email composer widths);
                 vertical overflow is intentionally unrestricted so tall panels like
                 NextActionPanel can expand and show their Confirm button. */}
            <Box sx={{ flex: 1, minWidth: 0, overflowX: 'hidden', display: 'flex', flexDirection: 'column' }} data-testid="action-panel-body">
                {(isEmail || isWhatsapp) ? (
                    // 2026-06-07 — Sidebar-redirect types (email + WhatsApp). The
                    // matching sidebar tab was opened on mount via onOpenSidebarTab
                    // in the useEffects above. Body renders a SidebarRedirectConfirmation
                    // so the user can mark the item done after sending from the sidebar.
                    // channel='whatsapp' is fully supported by CHANNEL_META +
                    // NO_SIDEBAR_META in SidebarRedirectConfirmation.jsx:51,69.
                    // Previously WHATSAPP_SENT fell through to PANEL_MAP.WHATSAPP_SENT
                    // → WhatsAppPanel → IosWhatsAppComposerDialog (a centred modal),
                    // confusing UX since the right-sidebar WhatsApp tab already exists
                    // on every host (FollowUpV2 + both Attended popups).
                    <SidebarRedirectConfirmation
                        channel={isEmail ? 'email' : 'whatsapp'}
                        body={isEmail
                            ? 'Email composer opened in the sidebar — compose and send your email there, then mark this done.'
                            : 'WhatsApp composer opened in the sidebar — send your message there. Use Resolve in WhatsApp if the conversation has an outcome.'}
                        doneLabel={isEmail ? 'Mark email sent' : 'Mark message sent'}
                        onComplete={() => { if (onComplete) onComplete(item); }}
                        practiceId={practiceId}
                        leadId={leadId}
                        leadDetail={leadDetail}
                        noSidebar={noSidebar}
                    />
                ) : (() => {
                    // Route to the correct panel via PANEL_MAP (lazy-loaded components)
                    const PanelComponent = PANEL_MAP[item.item_type];
                    if (PanelComponent) {
                        return (
                            <PanelComponent
                                item={item}
                                practiceId={practiceId}
                                leadId={leadId}
                                leadDetail={leadDetail}
                                onClose={onClose}
                                onComplete={onComplete}
                                handleReloadLeads={handleReloadLeads}
                                onOpenSidebarTab={onOpenSidebarTab}
                                noSidebar={noSidebar}
                                callScriptCategory={callScriptCategory}
                            />
                        );
                    }
                    // Defensive fallback for unknown action types
                    return <ComingSoonStub item={item} onClose={onClose} />;
                })()}
            </Box>
        </Box>
        </FocusTrap>
    );
}

ActionPanel.propTypes = {
    item: PropTypes.object,
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    leadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    leadDetail: PropTypes.object,
    onClose: PropTypes.func.isRequired,
    /** Called after panel success; orchestrator marks item done + refresh. */
    onComplete: PropTypes.func,
    /** Deferred table reload — called ~3s after archive dispatched so Celery drains first. */
    handleReloadLeads: PropTypes.func,
    /** Called with 'email' or 'whatsapp' to switch the right-sidebar to the matching tab. */
    onOpenSidebarTab: PropTypes.func,
    /** Additive, default false. When true (sidebarless host like the full-screen
     *  Completed-Treatment hub), email/WhatsApp confirmation copy points to the
     *  lead's detail tabs instead of "the sidebar". */
    noSidebar: PropTypes.bool,
    /** Call-script category forwarded to LogCallPanel's "View call script" button. */
    callScriptCategory: PropTypes.oneOf(['TCO', 'OUTBOUND', 'INBOUND']),
    hideHeader: PropTypes.bool,
};

ActionPanel.defaultProps = {
    item: null,
    practiceId: null,
    leadId: null,
    leadDetail: null,
    onComplete: null,
    handleReloadLeads: null,
    onOpenSidebarTab: null,
    noSidebar: false,
    callScriptCategory: 'OUTBOUND',
    hideHeader: false,
};
